import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, AlertTriangle, Copy, XCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { isActiveEmp } from "../../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/accidents`;
const ICON = { processing: Loader2, created: CheckCircle2, unmatched: AlertTriangle, duplicate: Copy, error: XCircle };
const TONE = { processing: "text-slate-500", created: "text-emerald-600", unmatched: "text-amber-600", duplicate: "text-slate-500", error: "text-red-600" };
const MSG = { pending: "بالانتظار", processing: "جارِ القراءة…", unmatched: "لم يُعرف المندوب — اختره", duplicate: "مكرر — موجود مسبقاً", error: "تعذّرت القراءة" };

const Unmatched = ({ r, employees, onCreated, i }) => {
  const [emp, setEmp] = useState("");
  const [pi, setPi] = useState(0);
  const [busy, setBusy] = useState(false);
  const x = r.extracted;
  const p = x.parties?.[pi] || {};
  const create = async () => {
    setBusy(true);
    try {
      const fault = p.fault_pct !== "" && !Number.isNaN(Number(p.fault_pct)) ? Math.min(100, Math.max(0, Number(p.fault_pct))) : x.fault_pct;
      const a = (await axios.post(API, { employee_id: emp, accident_date: x.accident_date, description: x.description, address: x.address, vehicle_label: p.plate || x.vehicle_label, report_number: x.report_number, fault_pct: p.fault_pct === undefined ? x.fault_pct : fault, report_file: r.file, parties: x.parties || null, party_index: pi })).data;
      onCreated(a);
    } catch (e) { toast.error(e?.response?.data?.detail || "حدث خطأ"); }
    setBusy(false);
  };
  return (
    <div className="mt-1 space-y-1">
      {!!x.parties?.length && (
        <select className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs" value={pi} onChange={(e) => setPi(Number(e.target.value))} data-testid={`bulk-acc-party-${i}`}>
          {x.parties.map((q, k) => <option key={k} value={k}>طرف المندوب: {q.name || "—"} ({q.id_number || "—"}) · {q.plate || "—"} · خطأ {q.fault_pct || "—"}%</option>)}
        </select>
      )}
      <div className="flex gap-1.5">
        <select className="h-8 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs" value={emp} onChange={(e) => setEmp(e.target.value)} data-testid={`bulk-acc-emp-${i}`}>
          <option value="">— اختر المندوب —</option>{employees.filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <Button size="sm" className="h-8 text-xs" disabled={!emp || busy} onClick={create} data-testid={`bulk-acc-create-${i}`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />} إنشاء الملف</Button>
      </div>
    </div>
  );
};

export const BulkReportsDialog = ({ open, onClose, employees, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);
  const set = (i, v) => setRows((p) => p.map((r, k) => (k === i ? { ...r, ...v } : r)));
  const run = async (files) => {
    if (!files.length) return;
    const base = rows.length;
    setRows((p) => [...p, ...files.map((f) => ({ name: f.name, status: "pending" }))]);
    setRunning(true);
    for (let k = 0; k < files.length; k++) {
      const i = base + k;
      set(i, { status: "processing" });
      const fd = new FormData(); fd.append("file", files[k]);
      try { const r = (await axios.post(`${API}/from-report`, fd)).data; set(i, r); if (r.status === "created") onSaved(); }
      catch (e) { set(i, { status: "error", detail: e?.response?.data?.detail }); }
    }
    setRunning(false);
  };
  const close = () => { if (running) return; setRows([]); onClose(); };
  const done = rows.filter((r) => r.status === "created").length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl font-body" data-testid="bulk-acc-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading">رفع تقارير حوادث مجمّعة</DialogTitle>
          <DialogDescription>ارفع تقارير نجم/المرور دفعة وحدة — الذكاء الاصطناعي يقرأ كل تقرير ويفتح ملف حادث للمندوب تلقائياً (بالهوية أو الاسم).</DialogDescription>
        </DialogHeader>
        <input ref={ref} type="file" multiple hidden accept="image/*,application/pdf" onChange={(e) => { run([...(e.target.files || [])]); e.target.value = ""; }} data-testid="bulk-acc-input" />
        <Button variant="outline" className="w-full gap-1.5 border-dashed py-6" disabled={running} onClick={() => ref.current?.click()} data-testid="bulk-acc-pick"><Upload className="h-4 w-4" /> اختر التقارير (صور أو PDF)</Button>
        {!!rows.length && <p className="text-xs text-slate-500" data-testid="bulk-acc-summary">تم إنشاء {done} من {rows.length}</p>}
        <div className="space-y-2">
          {rows.map((r, i) => {
            const I = ICON[r.status];
            return (
              <div key={i} className="rounded-lg border border-slate-200 p-2" data-testid={`bulk-acc-row-${i}`}>
                <div className="flex items-center gap-2 text-sm">
                  {I && <I className={`h-4 w-4 shrink-0 ${TONE[r.status]} ${r.status === "processing" ? "animate-spin" : ""}`} />}
                  <span className="truncate font-semibold">{r.name}</span>
                  <span className={`ms-auto shrink-0 text-xs ${TONE[r.status] || "text-slate-400"}`} data-testid={`bulk-acc-status-${i}`}>
                    {r.status === "created" ? `${r.accident.number} · ${r.accident.employee_name}` : r.status === "duplicate" ? `${MSG.duplicate} (${r.employee_name})` : r.detail || MSG[r.status]}
                  </span>
                </div>
                {r.status === "unmatched" && <Unmatched r={r} i={i} employees={employees} onCreated={(a) => { set(i, { status: "created", accident: a }); onSaved(); }} />}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
